/**
 * Date/Time utility functions for handling date and time storage and display
 * Stores timestamps for precise time tracking and dates for daily quests
 * 
 * This ensures consistent date/time handling for users worldwide.
 * 
 * Usage:
 * - Store timestamps: Use getCurrentTimestamp() for precise time
 * - Store dates: Use getTodayDate() for daily quests
 * - Display dates: Use formatDateForDisplay(dateString) for user-friendly formatting
 * - Display timestamps: Use formatTimestampForDisplay(timestamp) for precise time
 * - Check if today: Use isToday(dateString)
 * 
 * Example:
 * const timestamp = getCurrentTimestamp(); // Store precise time
 * const dateString = getTodayDate(); // Store daily date
 * const displayTime = formatTimestampForDisplay(timestamp); // Display to user
 */

/**
 * Get current timestamp for precise time tracking
 * @returns {string} - Current timestamp in ISO format
 */
export const getCurrentTimestamp = () => {
  return new Date().toISOString();
};

/**
 * Convert local date to date string for database storage
 * @param {Date} localDate - Local date object
 * @returns {string} - Date string in YYYY-MM-DD format (user's local date)
 */
export const localToDateString = (localDate = new Date()) => {
  const y = localDate.getFullYear();
  const m = String(localDate.getMonth() + 1).padStart(2, '0');
  const d = String(localDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/**
 * Convert date string from database back to local date string
 * @param {string} dateString - Date string from database (YYYY-MM-DD)
 * @returns {string} - Local date string in YYYY-MM-DD format
 */
export const dateStringToLocal = (dateString) => {
  if (!dateString) return localToDateString();
  
  // The date string is already in the user's local timezone
  return dateString;
};

/**
 * Get today's date in local time as date string for database storage
 * @returns {string} - Today's date in YYYY-MM-DD format
 */
export const getTodayDate = () => {
  return localToDateString(new Date());
};

/**
 * Get current local date string for display
 * @returns {string} - Current local date string (YYYY-MM-DD)
 */
export const getTodayLocal = () => {
  return localToDateString(new Date());
};

/**
 * Convert a date object to date string for database storage
 * @param {Date} date - Date object
 * @returns {string} - Date string
 */
export const dateToDateString = (date) => {
  return localToDateString(date);
};

/**
 * Convert date string to local Date object
 * @param {string} dateString - Date string
 * @returns {Date} - Local Date object
 */
export const dateStringToLocalDate = (dateString) => {
  if (!dateString) return new Date();
  
  return new Date(dateString + 'T00:00:00');
};

/**
 * Format date for display
 * @param {string} dateString - Date string from database
 * @param {string} format - Format string (default: 'YYYY-MM-DD')
 * @returns {string} - Formatted local date string
 */
export const formatLocalDate = (dateString, format = 'YYYY-MM-DD') => {
  const localDate = dateStringToLocalDate(dateString);
  
  if (format === 'YYYY-MM-DD') {
    return localToDateString(localDate);
  }
  
  // Add more format options as needed
  return localToDateString(localDate);
};

/**
 * Check if a date is today (in local time)
 * @param {string} dateString - Date string from database
 * @returns {boolean} - True if the date is today
 */
export const isToday = (dateString) => {
  const localDate = dateStringToLocalDate(dateString);
  const today = new Date();
  
  return localDate.toDateString() === today.toDateString();
};

/**
 * Get date range for a specific day (start and end in local time)
 * @param {string} dateString - Date string
 * @returns {Object} - { start: start of day, end: end of day }
 */
export const getDayRange = (dateString) => {
  const localDate = dateStringToLocalDate(dateString);
  
  // Start of day in local time
  const startOfDay = new Date(localDate);
  startOfDay.setHours(0, 0, 0, 0);
  
  // End of day in local time
  const endOfDay = new Date(localDate);
  endOfDay.setHours(23, 59, 59, 999);
  
  return {
    start: localToDateString(startOfDay),
    end: localToDateString(endOfDay)
  };
};

/**
 * Check if current time is past midnight (12:00 AM) for penalty quests
 * @returns {boolean} - True if past midnight
 */
export const isPastMidnight = () => {
  const now = new Date();
  return now.getHours() >= 0 && now.getHours() < 12;
};

/**
 * Get the current quest date (today if before midnight, yesterday if after)
 * @returns {string} - Date string for quests
 */
export const getCurrentQuestDate = () => {
  return getTodayDate();
};

/**
 * Check if quests should be in penalty mode (past midnight)
 * @returns {boolean} - True if quests should be penalties
 */
export const shouldShowPenalties = () => {
  const now = new Date();
  return now.getHours() >= 0 && now.getHours() < 12;
};

/**
 * Format timestamp for display with time
 * @param {string} timestamp - ISO timestamp from database
 * @param {string} format - Format type ('short', 'long', 'time-only')
 * @returns {string} - Formatted timestamp string
 */
export const formatTimestampForDisplay = (timestamp, format = 'short') => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  if (format === 'time-only') {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  if (format === 'long') {
    return date.toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  // Default short format with relative date
  const dateString = date.toDateString();
  const timeString = date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
  
  if (dateString === today.toDateString()) {
    return `Today at ${timeString}`;
  } else if (dateString === yesterday.toDateString()) {
    return `Yesterday at ${timeString}`;
  } else {
    return `${date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })} at ${timeString}`;
  }
};

/**
 * Format date for display with user-friendly formatting
 * @param {string} dateString - Date string from database
 * @param {string} format - Format type ('short', 'long', 'relative')
 * @returns {string} - Formatted date string
 */
export const formatDateForDisplay = (dateString, format = 'short') => {
  if (!dateString) return '';
  
  // Create a date object from the date string
  const localDate = new Date(dateString + 'T00:00:00');
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  
  if (format === 'relative') {
    if (localDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (localDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
  }
  
  if (format === 'long') {
    return localDate.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
  
  // Default short format
  return localDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}; 
